"use client";

import { Customer } from "@commercetools/platform-sdk";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useEffect } from "react";
import userflow, { Attributes } from "userflow.js";

import { getNewsletterSubscriberByEmail } from "@/clients/klaviyo/list";
import { config } from "@/config";
import { getNextLoyaltyRecalcDate } from "@/features/account/utils/get-next-loyalty-recalc-date";
import { useLoyaltyPrograms } from "@/features/checkout/hooks/use-cashback";
import { getLast30DaysSpend } from "../../app/actions";

export function useUserflow(customer: Customer | null | undefined): void {
  const { data: loyaltyPrograms, isLoading: isLoadingLoyaltyPrograms } = useLoyaltyPrograms(
    customer?.id,
    !!customer?.id,
  );

  useEffect(() => {
    if (config.NEXT_PUBLIC_USERFLOW_TOKEN) {
      userflow.init(config.NEXT_PUBLIC_USERFLOW_TOKEN);
    }
  }, []);

  useEffect(() => {
    if (customer === undefined) {
      return;
    }

    if (!customer?.id) {
      userflow.identifyAnonymous();
      return;
    }

    if (isLoadingLoyaltyPrograms) {
      return;
    }

    (async () => {
      const totalSpend = await getLast30DaysSpend(customer.id);
      const { data: subscriberList } = await getNewsletterSubscriberByEmail(customer.email);
      const { date } = getNextLoyaltyRecalcDate(loyaltyPrograms?.loyaltyTier?.joinDate);
      const loyaltyTierExpiryDate = date && date.toISOString();
      dayjs.extend(advancedFormat);
      const loyaltyTierExpiryDateFormatted = date && dayjs(date).format("Do MMMM");

      const attributes: Attributes = {
        name: [customer.firstName, customer.lastName].filter(Boolean).join(" "),
        email: customer.email,
        customerGroup: customer.custom?.fields?.sector,
        marketingPreference: !!subscriberList?.length ? "Opted-in" : "Opted-out",
        loyaltyTier: customer?.custom?.fields?.loyaltyTierKey,
        loyaltyVector: customer?.custom?.fields?.loyaltyVectorKey,
        loyaltyTierExpiryDate,
        loyaltyTierExpiryDateFormatted,
        last30DaysSpend: totalSpend,
        nextLoyaltyTierSpend: loyaltyPrograms?.loyaltyTier?.ledger.pointsToNextTier ?? 0,
        availableCashback: loyaltyPrograms?.cashbackWallet?.ledger.currentBalance ?? 0,
      };

      userflow.identify(customer.id, attributes);
    })();
  }, [customer, isLoadingLoyaltyPrograms]);
}
